import { Fragment, useEffect, useRef, useState } from 'react';
import { useFloating, offset, flip, shift, autoUpdate, Placement } from '@floating-ui/react';
import { useInView } from 'react-intersection-observer';
import { createPortal } from 'react-dom';
import { Container, Display, Footer, InputArea, List, ListItem, SelectedOption, Spacer, StackableArea, WidthRef } from './index.styles';
import { generateId } from '../../utils/helpers';
import { Icon } from '../../atoms/Icon';
import { Input, InputFeedbackType } from '../Input';
import { Button } from '../Button';
import { IconNames } from '../../../theme';

export const selectTypes = ['basic', 'stackable'] as const;
export const sizes = ['sm', 'md', 'lg', 'xl'] as const;

export interface Option {
  /**
   * Option action. The string returned upon option selection.
   */
  action: string;
  /**
   * Adds a line before this option. Optional.
   */
  isHighlighted?: boolean;
  /**
   * Option label text. Ex.: Translation.
   */
  label: string;
}

interface FooterButtonProps {
  /**
   * Icon name. If it has icon, icon will be applied. Optional. (You can see the full list at branding/icons)
   */
  icon?: IconNames;
  /**
   * Button text. Optional. (Leave it blank if you want only icon)
   */
  label?: string;
  /**
   * Emits an event to the invoker component when the button is clicked. Required.
   */
  onClick: () => void;
}

interface FeedbackProps extends InputFeedbackType {
  /**
   * Adds a duration time to the feedback in milliseconds. Optional.
   */
  duration?: number;
  /**
   * Emits an event to the invoker component when the feedback duration finishes. Optional.
   */
  onExpiration?: () => void;
}

interface InputProps {
  /**
   * Visual feedback message and type ('success', 'warning', 'info' or 'danger'). (E.g: {type: 'danger', message: 'Password is wrong'}). Optional.
   */
  feedback?: FeedbackProps;
  /**
   * Input max length. Optional.
   */
  maxLength?: number;
  /**
   * Placeholder text for the input. Required.
   */
  placeholder: string;
  /**
   * Emits an event to the invoker component when the input changes. Required.
   */
  onChange?: (option: string) => void;
  /**
   * Emits an event to the invoker component when the Enter key is pressed while focusing the input, sending the selected option. Required.
   */
  onEnter?: (option: Option) => void;
  /**
   * Input appearance and functionality may vary based on the selected property. Ex.: Basic has a normal input and can only select one option at a time. Stackable has an input and let you select multiple options. Required.
   */
  type: (typeof selectTypes)[number];
}

export interface AdvancedSelectProps extends Component {
  /**
   * Disable state attribute. Optional.
   */
  disabled?: boolean;
  /**
   * Makes list min-width the same as the display section. Optional.
   */
  fitList?: boolean;
  /**
   * Adds a Button component to the footer of the component. Optional.
   */
  footerButton?: FooterButtonProps;
  /**
   * Icon name. If it has icon, icon will be shown in the left side of the select. Optional. (You can see the full list at branding/icons)
   */
  icon?: IconNames;
  /**
   * Component ID. Required.
   */
  id: string;
  /**
   * Adds an input in the top of the component. Optional.
   */
  input?: InputProps;
  /**
   * Component label while no options are selected. Required.
   */
  label: string;
  /**
   * Sets the gap/distance between the display and the list. Optional.
   */
  listOffset?: number;
  /**
   * Max display label width. Optional.
   */
  maxWidth?: string;
  /**
   * Emits an event to the invoker component when an option is selected, sending the selected option. Required.
   */
  onSelect: (option: Option) => void;
  /**
   * Emits an event to the invoker component with all the selected options when using input stackable. Required.
   */
  onStackableSelect?: (options: Option[]) => void;
  /**
   * List of selectable options. Required.
   */
  options: Option[];
  /**
   * List opening position. Optional.
   */
  placement?: Placement;
  /**
   * List of options already selected. It should be an array when using input type stackable. Optional.
   */
  selectedOptions?: Option[];
  /**
   * Component size. Optional.
   */
  size?: (typeof sizes)[number];
}

export const AdvancedSelect = ({
  className,
  customClass,
  disabled,
  fitList,
  footerButton,
  icon,
  id,
  input,
  label,
  listOffset = 4,
  maxWidth,
  onSelect,
  onStackableSelect,
  options,
  placement = 'bottom-end',
  selectedOptions = [],
  size = 'md',
  testId = 'advanced-select',
}: AdvancedSelectProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [widthRefContent, setWidthRefContent] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLLIElement | null>(null);
  const widthRef = useRef<HTMLSpanElement | null>(null);

  const currentLabel = selectedOptions?.length === 1 ? selectedOptions[0].label : label;

  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    rootMargin: '8px',
  });

  const { x, y, strategy, refs } = useFloating({
    open: isCollapsed,
    onOpenChange: setIsCollapsed,
    placement,
    middleware: [offset(listOffset), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  /** Handle option selection */
  const handleSelect = (option: string) => {
    const item = options.find((opt) => opt.action === option) as Option;

    onSelect(item);
    setIsCollapsed(false);
  };

  /** Handle enter key events */
  const handleEnter = (option: string, source: 'input' | 'option') => {
    if (input?.feedback) return;

    if (input?.onEnter && source === 'input') input.onEnter({ action: option, label: option });
    else {
      const item = options.find((opt) => opt.action === option) as Option;

      onSelect(item);
    }

    setIsCollapsed(false);
  };

  /** Handle option selection when using input type stackable */
  const handleStackableSelection = (option: string, operation: 'add' | 'remove') => {
    if (!onStackableSelect) return;

    if (inputRef.current) {
      inputRef.current.value = '';
      setWidthRefContent(input?.placeholder || '');
    }

    if (operation === 'add') {
      const item = options.find((opt) => opt.action === option) as Option;

      onStackableSelect([...selectedOptions, item]);
    } else {
      const newOptions = selectedOptions.filter((opt) => opt.action !== option);

      onStackableSelect(newOptions);
    }
  };

  /** Handle enter key event for input type stackable */
  const handleStackableInputEnter = () => {
    if (inputRef?.current?.value && input?.onEnter && onStackableSelect) {
      const value = inputRef?.current?.value;
      const item = { action: value, label: value };

      onStackableSelect([...selectedOptions, item]);
      input.onEnter(item);
      inputRef.current.value = '';
      setWidthRefContent(input.placeholder);
    }
  };

  /** Handle key events */
  const handleKeyDown = (key: string, index: number, listLength: number, option: string) => {
    if (key === 'ArrowUp' && index > 0) {
      const { previousSibling } = document.activeElement;

      if (previousSibling.tabIndex == -1) previousSibling.previousSibling.focus();
      else previousSibling.focus();
    } else if (key === 'ArrowDown' && index < listLength - 1) {
      const { nextSibling } = document.activeElement;

      if (nextSibling.tabIndex == -1) nextSibling.nextSibling.focus();
      else nextSibling.focus();
    } else if (key === 'Enter') {
      if (input?.type !== 'stackable') handleEnter(option, 'option');
      else handleStackableSelection(option, 'add');
    } else if (key === 'Escape') setIsCollapsed(false);
  };

  /** Add event listeners to control blur */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (!target.id.includes(id)) setIsCollapsed(false);
    };

    document.addEventListener('mouseup', handleClickOutside);

    return () => document.removeEventListener('mouseup', handleClickOutside);
  }, []);

  /** Closes the select if the dropdown area isn't in the view */
  useEffect(() => {
    if (isCollapsed && !inView) setIsCollapsed(false);
  }, [inView]);

  /** Control focus */
  useEffect(() => {
    if (isCollapsed) {
      if (inputRef.current) inputRef.current.focus();
      else if (listRef.current) listRef.current.focus();
      setWidthRefContent(input?.placeholder || '');
    }
  }, [isCollapsed]);

  const optionList = options.map((option, index) => {
    const found = selectedOptions.find((opt) => opt.action === option.action);

    if (!found || input?.type !== 'stackable') {
      return (
        <Fragment key={generateId()}>
          {option.isHighlighted && (
            <Spacer
              role="presentation"
              tabIndex={-1}
              key={`spacer-${id}-${index}`}
            />
          )}
          <ListItem
            data-testid={`${testId}-li-${index}`}
            id={`${id}-li-${option.action}-${index}`}
            key={`li-${id}-${index}`}
            onClick={() => (input && input.type === 'stackable' ? handleStackableSelection(option.action, 'add') : handleSelect(option.action))}
            onKeyDown={(e) => handleKeyDown(e.key, index, optionList.length, option.action)}
            ref={(el) => {
              if (index === 0) listRef.current = el;
            }}
            role="menuitem"
            tabIndex={0}
            title={option.label}
            $selected={option.label === currentLabel}
          >
            {option.label}
          </ListItem>
        </Fragment>
      );
    }

    return null;
  });

  return (
    <Container
      className={`ds-advanced-select ds-advanced-select-${size} ${customClass} ${className}`}
      data-testid={testId}
      $disabled={!!disabled}
      $size={size}
      id={id}
    >
      {/** SELECT DISPLAY AREA */}
      <Display
        className={`ds-advanced-select-display ds-advanced-select-${size}-display`}
        data-testid={`${testId}-display`}
        $disabled={!!disabled}
        $hasIcon={!!icon}
        $isCollapsed={isCollapsed}
        id={`${id}-display`}
        $maxWidth={maxWidth}
        onClick={() => setIsCollapsed((prev) => !prev)}
        onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && setIsCollapsed((prev) => !prev)}
        ref={refs.setReference}
        tabIndex={0}
      >
        {icon && (
          <Icon
            color="brandDark"
            id={`${id}-display-icon`}
            name={icon}
            strokeWidth={2}
          />
        )}
        <span
          id={`${id}-label`}
          data-testid={`${testId}-label`}
        >
          {currentLabel}
        </span>
        <Icon
          color="brandDark"
          id={`${id}-chevron`}
          name="chevron_down"
          className="icon ds-advanced-select-display-chevron"
          strokeWidth={2}
        />
      </Display>

      {/** DROPDOWN AREA */}
      {isCollapsed &&
        !disabled &&
        createPortal(
          <List
            className={`ds-advanced-select-list ds-advanced-select-${size}-list`}
            data-testid={`${testId}-list`}
            id={`${id}-list`}
            $minWidth={fitList ? refs.reference.current?.getBoundingClientRect().width : 0}
            $useMaxWidth={input?.type === 'stackable'}
            ref={refs.setFloating}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
            tabIndex={-1}
          >
            <div
              id={`${id}-content`}
              tabIndex={-1}
            >
              {/** BASIC INPUT */}
              {input && input.type === 'basic' && (
                <InputArea
                  $hasError={input.feedback?.type === 'danger'}
                  data-testid={`${testId}-inputarea`}
                >
                  <Input
                    autoComplete="off"
                    change={(v) => input.onChange && input.onChange(v)}
                    feedback={input.feedback ? { message: input.feedback.message, type: input.feedback.type } : undefined}
                    feedbackDuration={input.feedback?.duration}
                    label={input.placeholder}
                    id={`${id}-input`}
                    keyPress={(key, value) => key === 'Enter' && handleEnter(value, 'input')}
                    maxLength={input.maxLength || undefined}
                    onFeedbackDurationEnd={input.feedback?.onExpiration}
                    ref={inputRef}
                    size="sm"
                  />
                </InputArea>
              )}

              {/** STACKABLE INPUT */}
              {input && input.type === 'stackable' && (
                <StackableArea
                  $inputWidth={widthRef.current?.offsetWidth || 0}
                  data-testid={`${testId}-inputarea`}
                >
                  {selectedOptions?.map((option, index) => (
                    <SelectedOption
                      data-testid={`${testId}-selected-option-${index}`}
                      key={`option-${generateId()}`}
                      id={`so-${id}`}
                    >
                      <span id={`so-${id}-label-${generateId()}`}>{option.label}</span>
                      <Button
                        data-testid={`${testId}-selected-option-${index}-remover`}
                        icon="close_lg"
                        id={`so-${id}-btn-${generateId()}`}
                        onClick={() => handleStackableSelection(option.action, 'remove')}
                        size={12}
                      />
                    </SelectedOption>
                  ))}
                  <WidthRef ref={widthRef}>{widthRefContent}</WidthRef>
                  <input
                    autoComplete="off"
                    id={`${id}-input`}
                    maxLength={input.maxLength || undefined}
                    onChange={(e) => {
                      if (input.onChange) input.onChange(e.target.value);
                      setWidthRefContent(e.target.value);
                    }}
                    onKeyPress={({ key }) => key === 'Enter' && handleStackableInputEnter()}
                    placeholder={input.placeholder}
                    ref={inputRef}
                    type="text"
                  />
                </StackableArea>
              )}

              {/** OPTION LIST */}
              <ul
                aria-labelledby={`swf-${label}`}
                id={`${id}-ul`}
                ref={inViewRef}
                role="menu"
                tabIndex={-1}
              >
                {optionList}
              </ul>

              {/** FOOTER */}
              {footerButton && (
                <Footer className={`ds-advanced-select-footer ds-advanced-select-${size}-footer`}>
                  <Button
                    appearance="primary"
                    data-testid={`${testId}-footer-button`}
                    icon={footerButton?.icon}
                    id={`${id}-footer-button`}
                    label={footerButton?.label}
                    onClick={footerButton?.onClick}
                    size={12}
                  />
                </Footer>
              )}
            </div>
          </List>,
          document.getElementById('dropdown-root') as HTMLElement
        )}
    </Container>
  );
};
